/**
 * StatusBadge Component
 *
 * A specialized badge for displaying operation statuses with appropriate
 * icons, colors, and optional animation for in-progress states.
 */

import React from 'react';
import {
  Clock,
  FileSearch,
  Eye,
  CheckCircle,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Ban,
  RotateCcw,
  Undo2,
  Loader2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { OperationStatus } from '@/lib/services/bulk-operations/types';

// =============================================================================
// TYPES
// =============================================================================

export interface StatusBadgeProps {
  /** The operation status to display */
  status: OperationStatus;
  /** Optional size override */
  size?: 'sm' | 'md' | 'lg';
  /** Show icon with the status text */
  showIcon?: boolean;
  /** Show only the icon (no text) */
  iconOnly?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// =============================================================================
// STATUS CONFIGURATION
// =============================================================================

interface StatusConfig {
  label: string;
  icon: React.ElementType;
  variant: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  animate?: boolean;
}

const STATUS_CONFIG: Record<OperationStatus, StatusConfig> = {
  pending: {
    label: 'Pending',
    icon: Clock,
    variant: 'secondary',
  },
  parsing: {
    label: 'Parsing',
    icon: FileSearch,
    variant: 'info',
    animate: true,
  },
  previewing: {
    label: 'Previewing',
    icon: Eye,
    variant: 'info',
    animate: true,
  },
  confirmed: {
    label: 'Confirmed',
    icon: CheckCircle,
    variant: 'primary',
  },
  processing: {
    label: 'Processing',
    icon: Loader2,
    variant: 'info',
    animate: true,
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    variant: 'success',
  },
  partial_success: {
    label: 'Partial Success',
    icon: AlertTriangle,
    variant: 'warning',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    variant: 'error',
  },
  cancelled: {
    label: 'Cancelled',
    icon: Ban,
    variant: 'secondary',
  },
  skipped: {
    label: 'Skipped',
    icon: Ban,
    variant: 'secondary',
  },
  rolling_back: {
    label: 'Rolling Back',
    icon: RotateCcw,
    variant: 'warning',
    animate: true,
  },
  rolled_back: {
    label: 'Rolled Back',
    icon: Undo2,
    variant: 'default',
  },
  partially_rolled_back: {
    label: 'Partially Rolled Back',
    icon: AlertTriangle,
    variant: 'warning',
  },
};

// =============================================================================
// COMPONENT
// =============================================================================

export function StatusBadge({
  status,
  size = 'md',
  showIcon = true,
  iconOnly = false,
  className,
}: StatusBadgeProps) {
  const config = STATUS_CONFIG[status];

  if (!config) {
    // Fallback for unknown status
    return (
      <Badge variant="default" size={size} className={className}>
        {status}
      </Badge>
    );
  }

  const { label, icon: Icon, variant, animate } = config;

  const iconSize = size === 'sm' ? 12 : size === 'lg' ? 16 : 14;

  return (
    <Badge
      variant={variant}
      size={size}
      className={cn('gap-1.5', className)}
      title={iconOnly ? label : undefined}
    >
      {showIcon && (
        <Icon
          size={iconSize}
          className={cn(animate && 'animate-spin')}
        />
      )}
      {!iconOnly && <span>{label}</span>}
    </Badge>
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get the display label for an operation status
 */
export function getStatusLabel(status: OperationStatus): string {
  return STATUS_CONFIG[status]?.label ?? status;
}

/**
 * Check if an operation is in a terminal state
 */
export function isTerminalStatus(status: OperationStatus): boolean {
  return ['completed', 'partial_success', 'failed', 'cancelled', 'skipped', 'rolled_back', 'partially_rolled_back'].includes(status);
}

/**
 * Check if an operation is in a processing state
 */
export function isProcessingStatus(status: OperationStatus): boolean {
  return ['parsing', 'previewing', 'processing', 'rolling_back'].includes(status);
}

/**
 * Check if an operation can be cancelled
 */
export function isCancellable(status: OperationStatus): boolean {
  return ['pending', 'parsing', 'previewing', 'confirmed', 'processing'].includes(status);
}

export default StatusBadge;
